import type { DatabaseAiFieldRunAwareness } from '@databases/collab-state-manager/y-database-telepointers';

export type AiFieldRunTarget = {
  aiFieldType: string;
  fieldId: string;
};

export type AiFieldCellTarget = AiFieldRunTarget & {
  entryId: string;
};

const isAiFieldRunTargetingEntry = (
  run: DatabaseAiFieldRunAwareness,
  {
    aiFieldType,
    fieldId,
    entryId,
    isTargetValueEmpty
  }: AiFieldCellTarget & { isTargetValueEmpty: boolean }
): boolean => {
  if (run.aiFieldType !== aiFieldType || run.fieldId !== fieldId) {
    return false;
  }

  switch (run.scope.kind) {
    case 'entry':
      return run.scope.entryId === entryId;
    case 'entries':
      return run.scope.entryIds.includes(entryId);
    case 'field':
      return run.scope.operation === 'all-entries' || isTargetValueEmpty;
    default: {
      const unhandledScope: never = run.scope;
      throw new Error(`Unhandled AI field run scope: ${unhandledScope}`);
    }
  }
};

export const hasActiveAiFieldRunForEntry = (
  aiFieldRuns: Readonly<Record<number, DatabaseAiFieldRunAwareness>>,
  target: AiFieldCellTarget & { isTargetValueEmpty: boolean }
): boolean =>
  Object.values(aiFieldRuns).some((run) => isAiFieldRunTargetingEntry(run, target));

const doAiFieldRunScopesOverlap = (
  left: DatabaseAiFieldRunAwareness['scope'],
  right: DatabaseAiFieldRunAwareness['scope']
): boolean => {
  if (left.kind === 'field' || right.kind === 'field') {
    return true;
  }

  const leftEntryIds = left.kind === 'entry' ? [left.entryId] : left.entryIds;
  const rightEntryIds = right.kind === 'entry' ? [right.entryId] : right.entryIds;
  const rightEntryIdSet = new Set(rightEntryIds);

  return leftEntryIds.some((entryId) => rightEntryIdSet.has(entryId));
};

export const hasOverlappingAiFieldRun = (
  aiFieldRuns: Readonly<Record<number, DatabaseAiFieldRunAwareness>>,
  target: AiFieldRunTarget & { scope: DatabaseAiFieldRunAwareness['scope'] }
): boolean =>
  Object.values(aiFieldRuns).some(
    (run) =>
      run.aiFieldType === target.aiFieldType &&
      run.fieldId === target.fieldId &&
      doAiFieldRunScopesOverlap(run.scope, target.scope)
  );
